import { API_BASE } from '../config'
import { useEffect, useState, useMemo } from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts'

interface LogisticaData {
  total_entregues: number
  entregues_por_estado: Array<{ estado: string; quantidade: number }>
  ranking_marcas: Array<{ marca: string; quantidade: number }>
  raquetes_entrada: number
}

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
]

const BRAND_COLORS = [
  '#3b82f6','#a855f7','#10b981','#f59e0b','#ef4444',
  '#06b6d4','#84cc16','#f97316','#ec4899','#6366f1'
]

export default function Logistica() {
  const [data, setData] = useState<LogisticaData | null>(null)
  const [loading, setLoading] = useState(false)
  const [mes, setMes] = useState(6)
  const [expandedEstados, setExpandedEstados] = useState(false)

  // Comparativo mensal
  const [mes1, setMes1] = useState(5)
  const [mes2, setMes2] = useState(6)
  const [dataComp1, setDataComp1] = useState<LogisticaData | null>(null)
  const [dataComp2, setDataComp2] = useState<LogisticaData | null>(null)
  const [loadingComp, setLoadingComp] = useState(false)

  const ano = 2026

  const fetchLogisticaData = async (month: number): Promise<LogisticaData | null> => {
    try {
      const response = await fetch(`${API_BASE}/api/kpis/logistica?mes=${month}&ano=${ano}`)
      if (!response.ok) throw new Error('Erro ao buscar dados')
      const result = await response.json()
      return result.data
    } catch (err) {
      console.error('Erro ao buscar logística:', err)
      return null
    }
  }

  useEffect(() => {
    setLoading(true)
    fetchLogisticaData(mes).then(d => { setData(d); setLoading(false) })
  }, [mes])

  useEffect(() => {
    setLoadingComp(true)
    Promise.all([fetchLogisticaData(mes1), fetchLogisticaData(mes2)]).then(([d1, d2]) => {
      setDataComp1(d1)
      setDataComp2(d2)
      setLoadingComp(false)
    })
  }, [mes1, mes2])

  // Combina marcas dos dois meses para o gráfico de barras agrupadas
  const marcasComparacao = useMemo(() => {
    if (!dataComp1 || !dataComp2) return []
    const allMarcas = new Set([
      ...dataComp1.ranking_marcas.map(m => m.marca),
      ...dataComp2.ranking_marcas.map(m => m.marca)
    ])
    return Array.from(allMarcas)
      .map(marca => {
        const q1 = dataComp1.ranking_marcas.find(m => m.marca === marca)?.quantidade || 0
        const q2 = dataComp2.ranking_marcas.find(m => m.marca === marca)?.quantidade || 0
        const crescimento = q1 > 0 ? ((q2 - q1) / q1) * 100 : q2 > 0 ? 100 : 0
        return { marca, [MESES[mes1-1]]: q1, [MESES[mes2-1]]: q2, crescimento }
      })
      .sort((a, b) => (b[MESES[mes2-1]] as number) - (a[MESES[mes2-1]] as number))
  }, [dataComp1, dataComp2, mes1, mes2])

  // Ranking de crescimento (apenas marcas presentes em ambos os meses)
  const rankingCrescimento = useMemo(() => {
    return marcasComparacao
      .filter(m => (m[MESES[mes1-1]] as number) > 0 && (m[MESES[mes2-1]] as number) > 0)
      .sort((a, b) => b.crescimento - a.crescimento)
  }, [marcasComparacao, mes1, mes2])

  if (loading && !data) {
    return <div className="p-6 text-gray-500">Carregando dados...</div>
  }

  const label1 = MESES[mes1 - 1]
  const label2 = MESES[mes2 - 1]

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Logística</h1>
          <select
            value={mes}
            onChange={(e) => setMes(parseInt(e.target.value))}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {new Date(2026, m - 1).toLocaleString('pt-BR', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>

        {data && (
          <>
            {/* KPI CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-sm font-medium text-blue-600 dark:text-blue-300 mb-2">Total de Entregas</p>
                <p className="text-4xl font-bold text-blue-900 dark:text-blue-100">{data.total_entregues}</p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-2">Quantidade total de pedidos processados</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-lg border border-purple-200 dark:border-purple-700">
                <p className="text-sm font-medium text-purple-600 dark:text-purple-300 mb-2">Raquetes de Entrada</p>
                <p className="text-4xl font-bold text-purple-900 dark:text-purple-100">{data.raquetes_entrada}</p>
                <p className="text-xs text-purple-600 dark:text-purple-300 mt-2">Quantidade recebida de entrada</p>
              </div>
            </div>

            {/* ENTREGAS POR ESTADO */}
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 border border-gray-200 dark:border-slate-700 mb-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                {expandedEstados ? 'Todos os Estados' : 'Top 3 Estados'}
              </h2>
              {data.entregues_por_estado.length > 0 ? (
                <>
                  <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${expandedEstados ? 'grid-cols-1' : ''}`}>
                    {data.entregues_por_estado.slice(0, expandedEstados ? undefined : 3).map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border-2 ${
                          !expandedEstados && idx < 3
                            ? idx === 0
                              ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                              : idx === 1
                              ? 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
                            : 'bg-gray-50 dark:bg-slate-800 border-gray-300 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {!expandedEstados && idx < 3 && (
                              <span className="text-2xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                            )}
                            <div>
                              <span className={`text-lg font-bold ${
                                !expandedEstados && idx < 3
                                  ? idx === 0 ? 'text-yellow-600 dark:text-yellow-400'
                                  : idx === 1 ? 'text-gray-600 dark:text-gray-400'
                                  : 'text-orange-600 dark:text-orange-400'
                                  : 'text-gray-900 dark:text-white'
                              }`}>{item.estado}</span>
                              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">entregas</p>
                            </div>
                          </div>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.quantidade}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {data.entregues_por_estado.length > 3 && (
                    <button
                      onClick={() => setExpandedEstados(!expandedEstados)}
                      className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition"
                    >
                      {expandedEstados ? 'Ver Menos' : `Ver Mais (${data.entregues_por_estado.length - 3} restantes)`}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">Nenhum dado disponível</p>
              )}
            </div>

            {/* RANKING DE MARCAS */}
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ranking de Marcas Vendidas</h2>
              {data.ranking_marcas.length > 0 ? (
                <div className="space-y-2">
                  {data.ranking_marcas.map((item, idx) => {
                    const pct = (item.quantidade / data.ranking_marcas[0].quantidade) * 100
                    return (
                      <div key={idx} className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-500 dark:text-gray-400 w-6">{idx + 1}º</span>
                            <span className="font-medium text-gray-900 dark:text-white">{item.marca}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 dark:text-white bg-blue-100 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                            {item.quantidade} vendas
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full"
                            style={{ width: `${pct}%`, backgroundColor: BRAND_COLORS[idx % BRAND_COLORS.length] }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">Nenhum dado disponível</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* ===== COMPARATIVO MENSAL DE MARCAS ===== */}
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 pb-6 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Comparativo Mensal de Marcas</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Evolução das marcas mais vendidas entre dois meses</p>
          </div>
          <div className="flex gap-3 items-center">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mês 1</label>
              <select
                value={mes1}
                onChange={(e) => setMes1(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{MESES[m-1]}</option>
                ))}
              </select>
            </div>
            <div className="text-gray-400 dark:text-gray-500 font-bold mt-4">vs</div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mês 2</label>
              <select
                value={mes2}
                onChange={(e) => setMes2(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{MESES[m-1]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loadingComp ? (
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            Carregando comparativo...
          </div>
        ) : marcasComparacao.length > 0 && (
          <>
            {/* KPI RESUMO DO COMPARATIVO */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-xs text-blue-600 dark:text-blue-300 mb-1">Total {label1}</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {dataComp1?.total_entregues ?? 0}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300">entregas</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
                <p className="text-xs text-purple-600 dark:text-purple-300 mb-1">Total {label2}</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {dataComp2?.total_entregues ?? 0}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-300">entregas</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
                <p className="text-xs text-green-600 dark:text-green-300 mb-1">Marca destaque {label2}</p>
                <p className="text-lg font-bold text-green-900 dark:text-green-100 truncate">
                  {marcasComparacao[0]?.marca ?? '—'}
                </p>
                <p className="text-xs text-green-600 dark:text-green-300">
                  {(marcasComparacao[0]?.[label2] as number) ?? 0} vendas
                </p>
              </div>
              <div className={`p-4 rounded-lg border ${
                (() => {
                  const t1 = dataComp1?.total_entregues ?? 0
                  const t2 = dataComp2?.total_entregues ?? 0
                  return t2 >= t1
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                })()
              }`}>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Variação total</p>
                {(() => {
                  const t1 = dataComp1?.total_entregues ?? 0
                  const t2 = dataComp2?.total_entregues ?? 0
                  const pct = t1 > 0 ? ((t2 - t1) / t1) * 100 : 0
                  const pos = pct >= 0
                  return (
                    <>
                      <p className={`text-2xl font-bold ${pos ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                        {pos ? '+' : ''}{pct.toFixed(1)}%
                      </p>
                      <p className={`text-xs ${pos ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
                        {label1} → {label2}
                      </p>
                    </>
                  )
                })()}
              </div>
            </div>

            {/* GRÁFICO BARRAS AGRUPADAS - Marcas por Mês */}
            <div className="bg-gray-50 dark:bg-slate-800 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Marcas Mais Vendidas — {label1} vs {label2}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Quantidade de vendas por marca nos dois meses</p>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={marcasComparacao} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="marca"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    angle={-30}
                    textAnchor="end"
                    interval={0}
                    height={55}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                    formatter={(v: any) => [`${v} vendas`]}
                  />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Bar dataKey={label1} fill="#9ca3af" radius={[4,4,0,0]} />
                  <Bar dataKey={label2} fill="#3b82f6" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* RANKING DE CRESCIMENTO POR MARCA */}
            <div className="bg-gray-50 dark:bg-slate-800 p-6 rounded-xl border border-gray-200 dark:border-slate-700 mb-8">
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Crescimento por Marca — {label1} → {label2}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">Variação percentual de vendas entre os meses (marcas em ambos os meses)</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={rankingCrescimento}
                  margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`}
                  />
                  <YAxis
                    type="category"
                    dataKey="marca"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    width={90}
                  />
                  <Tooltip
                    formatter={(v: any) => [`${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}%`, 'Crescimento']}
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#fff' }}
                  />
                  <Bar dataKey="crescimento" radius={[0,4,4,0]}>
                    {rankingCrescimento.map((entry, idx) => (
                      <Cell key={idx} fill={entry.crescimento >= 0 ? '#10b981' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* TABELA DETALHADA */}
            <div className="bg-gray-50 dark:bg-slate-800 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
                <h3 className="font-bold text-gray-900 dark:text-white">Tabela Comparativa por Marca</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-slate-700">
                      <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Marca</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">{label1}</th>
                      <th className="px-4 py-3 text-center font-medium text-blue-600 dark:text-blue-400">{label2}</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">Crescimento</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">Diferença</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marcasComparacao.map((item, idx) => {
                      const q1 = item[label1] as number
                      const q2 = item[label2] as number
                      const diff = q2 - q1
                      const pos = item.crescimento >= 0
                      const isNew = q1 === 0 && q2 > 0
                      const disappeared = q1 > 0 && q2 === 0
                      return (
                        <tr key={idx} className="border-t border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_COLORS[idx % BRAND_COLORS.length] }} />
                              <span className="font-medium text-gray-900 dark:text-white">{item.marca}</span>
                              {isNew && <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">Nova</span>}
                              {disappeared && <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded-full">Saiu</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{q1}</td>
                          <td className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">{q2}</td>
                          <td className="px-4 py-3 text-center">
                            {isNew || disappeared ? (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                            ) : (
                              <div className={`flex items-center justify-center gap-1 font-bold ${pos ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {pos ? <TrendingUp size={14} /> : item.crescimento === 0 ? <Minus size={14} /> : <TrendingDown size={14} />}
                                {pos ? '+' : ''}{item.crescimento.toFixed(1)}%
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm font-medium ${diff > 0 ? 'text-green-600 dark:text-green-400' : diff < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
                              {diff > 0 ? '+' : ''}{diff}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* CARDS RESUMO */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {rankingCrescimento.length > 0 && (
                <>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
                    <p className="text-xs text-green-600 dark:text-green-300 mb-1">Maior crescimento</p>
                    <p className="text-lg font-bold text-green-900 dark:text-green-100">{rankingCrescimento[0].marca}</p>
                    <p className="text-sm text-green-700 dark:text-green-200">
                      +{rankingCrescimento[0].crescimento.toFixed(1)}% ({rankingCrescimento[0][label1]} → {rankingCrescimento[0][label2]})
                    </p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-700">
                    <p className="text-xs text-red-600 dark:text-red-300 mb-1">Maior queda</p>
                    <p className="text-lg font-bold text-red-900 dark:text-red-100">{rankingCrescimento[rankingCrescimento.length - 1].marca}</p>
                    <p className="text-sm text-red-700 dark:text-red-200">
                      {rankingCrescimento[rankingCrescimento.length - 1].crescimento.toFixed(1)}% ({rankingCrescimento[rankingCrescimento.length - 1][label1]} → {rankingCrescimento[rankingCrescimento.length - 1][label2]})
                    </p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                    <p className="text-xs text-blue-600 dark:text-blue-300 mb-1">Crescimento médio</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                      {(rankingCrescimento.reduce((s, m) => s + m.crescimento, 0) / rankingCrescimento.length).toFixed(1)}%
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">média entre {rankingCrescimento.length} marcas</p>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
